import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'

import { RecType } from 'constants/constants'

import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { LoadingOverlay } from 'components/commonparts'
import { useGridBase } from 'hooks/useGridBase'
import _ from 'lodash'
import { convertFromDate, dateToStr24HPad0DayOfWeek } from 'utils/convertFromLocalDate'

import ShowLastUpdateDatetime from '../ShowLastUpdateDatetime/ShowLastUpdateDatetime'
import ExpectedRankTooltip from '../Tooltip/ExpectedRankTooltip'

import type { ICellRendererParams, ManagedGridOptions } from 'ag-grid-community'
import type {
  GetShipExpectedShipExpectedGetRequest,
  ShipExpected,
  ShipExpectedCustomer,
  ShipExpecteds,
} from 'api-client'

import 'utils/mst.scss'

// Constants
const GRID_CONFIG = {
  ROW_HEIGHT: 36,
  RATING_MAX: 3,
  CONTAINER_HEIGHT: {
    treepassItem: 'calc(100vh - 200px)',
    treepassCenter: 'calc(100vh - 310px)'
  },
  ITEM_DETAIL_HEIGHT: {
    collapsed: 80,  // ItemDetail collapsed height
    expanded: 400   // ItemDetail expanded height
  }
} as const

const GROUP_HEADER_CLASS_MAP: Record<string, string> = {
  [RecType.Rec]: 'value-header-rec',
  [RecType.Today]: 'value-header-today',
  [RecType.Expected]: 'value-header-expected',
} as const

// Types
interface GridParams {
  request: {
    groupKeys: string[]
    filterModel?: any
  }
  success: (data: { rowData: any[] }) => void
  fail: () => void
}

interface ProcessedRowData {
  group: boolean
  [key: string]: any
}

// Custom Rating Component
const CustomRating = React.memo(({ value, max = GRID_CONFIG.RATING_MAX }: { value: number; max?: number }) => {
  const stars = []
  for (let i = 1; i <= max; i += 1) {
    stars.push(
      <span
        key={i}
        style={{
          color: i <= value ? '#ffc107' : '#e0e0e0',
          fontSize: '18px',
          marginRight: '2px',
        }}
      >
        ★
      </span>
    )
  }

  return <div style={{ display: 'flex', alignItems: 'center' }}>{stars}</div>
})

CustomRating.displayName = 'CustomRating'

// Rating Renderer Component
const RatingRenderer = React.memo((params: ICellRendererParams) => {
  const { value } = params
  return <CustomRating value={value || 0} max={GRID_CONFIG.RATING_MAX} />
})

RatingRenderer.displayName = 'RatingRenderer'

// Tree Data Renderer Component
const TreeDataRenderer = React.memo((params: ICellRendererParams) => {
  const { data } = params
  if (!data) return null
  
  const hasChildren = data.rootpass || false
  
  let displayText = ''
  if (data.itemName) {
    displayText = data.itemCode 
      ? `${data.itemName} (ID：${data.itemCode})` 
      : data.itemName
  } 
  if (data.centerName) {
    displayText = data.centerId 
      ? `${data.centerName} (ID：${data.centerId})` 
      : data.centerName
  }
  
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
    }
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {hasChildren && (
        <span 
          style={{ marginRight: 8, cursor: 'pointer' }}
          onClick={() => {}}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
        >
          ▶
        </span>
      )}
      <span>{displayText}</span>
    </div>
  )
})

TreeDataRenderer.displayName = 'TreeDataRenderer'

export interface AddFieldTreePass extends ShipExpectedCustomer {
  id: string
  treepassItem?: string[]
  treepassCenter?: string[]
  displayName?: string
  rootpass?: boolean
}

interface ShipExpectedDataListProps {
  list: ShipExpecteds
  isLoading: boolean
  getChildRows: (
    requestParam: GetShipExpectedShipExpectedGetRequest,
    rootTreePath: string
  ) => Promise<void | AddFieldTreePass[] | undefined>
  treepassKey: string
  isItemDetailExpanded?: boolean
}

function ShipExpectedDataList({ list, isLoading, getChildRows, treepassKey, isItemDetailExpanded = false }: ShipExpectedDataListProps) {
  const gridRef = useRef<AgGridReact>(null)
  const [columns, setColumns] = useState<any[]>([])
  const [rowData, setRowData] = useState<any[]>([])
  const [processedList, setProcessedList] = useState<any[]>([])

  // API may return daylist out of order; always sort by dateno asc for stable column order
  const sortedDayList = useMemo(() => {
    const daylist = list?.daylist ?? []
    return [...daylist].sort((a: any, b: any) => (a?.dateno ?? 0) - (b?.dateno ?? 0))
  }, [list?.daylist])

  const {
    numberValueFormatter,
    formatDecimalValue,
    onGridReady: baseOnGridReady,
    localeText,
    autoSizeStrategy,
  } = useGridBase({
    gridRef,
    isLoading,
    preferColDefWidth: false, // Allow autoSizeStrategy to work for reset functionality
  })

  // Create displayText for each item to easily filter
  useEffect(() => {
    const processed = list.list.map(item => {
      let displayText = ''
      const itemAny = item as any
      
      if (itemAny.itemName) {
        displayText = itemAny.itemCode 
          ? `${itemAny.itemName} (ID：${itemAny.itemCode})` 
          : itemAny.itemName
      } else if (itemAny.centerName) {
        displayText = itemAny.centerId 
          ? `${itemAny.centerName} (ID：${itemAny.centerId})` 
          : itemAny.centerName
      } else if (itemAny.customerCompanyName) {
        displayText = itemAny.customerCompanyId 
          ? `${itemAny.customerCompanyName} (ID：${itemAny.customerCompanyId})` 
          : itemAny.customerCompanyName
      }
      
      return {
        ...item,
        displayText
      }
    })
    
    setProcessedList(processed)
  }, [list.list])

  // Memoized data validation
  const hasValidData = useMemo(() => 
    processedList.length > 0 && processedList.some(item => 
      sortedDayList.some((dayItem: any) => 
        (item as any)[dayItem.colname!] !== null && 
        (item as any)[dayItem.colname!] !== undefined && 
        (item as any)[dayItem.colname!] !== ''
      )
    )
  , [processedList, sortedDayList])

  // Memoized CSS class utility
  const getCellClasses = useCallback((params: any) => {
    const { colDef, data } = params
    const { field } = colDef
    
    if (!data || !field) return ['number-cell']
    
    const dayListItem = _.find(sortedDayList, { colname: field })
    if (!dayListItem) return ['number-cell']
    
    const classes = ['number-cell']
    
    if (dayListItem.rectype === RecType.Today) {
      classes.push('todays-order-cell')
    }
    
    if (dayListItem.datevalue) {
      const datevalue = new Date(dayListItem.datevalue)
      if (convertFromDate(datevalue) === convertFromDate(data.systemDate)) {
        classes.push('todays-order-cell')
      }
    }
    
    return classes
  }, [sortedDayList])

  // Server-side datasource for valid data
  const createServerSideDatasource = useCallback(() => ({
    getRows: (params: GridParams) => {
      const { request } = params
      const { groupKeys, filterModel } = request as any
      
      if (groupKeys.length === 0) {
        // Root level - return main data
        let filteredData = processedList

        // Apply filter if exists
        if (filterModel && Object.keys(filterModel).length > 0) {
          // Check for ag-Grid-AutoColumn filter (this is the auto group column)
          if (filterModel['ag-Grid-AutoColumn']) {
            const filter = filterModel['ag-Grid-AutoColumn']

            // Handle multi filter (agMultiColumnFilter)
            if (filter.filterType === 'multi' && filter.filterModels && filter.filterModels.length > 0) {
              // Get the first filter model (set filter)
              const setFilter = filter.filterModels[0]
              if (setFilter.values !== undefined) {
                if (setFilter.values.length > 0) {
                  // There are items selected - filter by selection
                  filteredData = processedList.filter(item => {
                    const displayText = item.displayText || ''
                    const isIncluded = setFilter.values.includes(displayText)
                    return isIncluded
                  })
                } else {
                  // Empty array - no item selected, hide all
                  filteredData = []
                }
              }
            }
            // Handle single filter (legacy)
            else if (filter.values && filter.values.length > 0) {
              filteredData = processedList.filter(item => {
                const displayText = item.displayText || ''
                const isIncluded = filter.values.includes(displayText)
                return isIncluded
              })
            }
            // Handle text filter
            else if (filter.filter) {
              const filterValue = filter.filter
              filteredData = processedList.filter(item => {
                const displayText = item.displayText || ''
                
                let matches = false
                switch (filter.type) {
                  case 'contains':
                    matches = displayText.toLowerCase().includes(filterValue.toLowerCase())
                    break
                  case 'equals':
                    matches = displayText === filterValue
                    break
                  case 'startsWith':
                    matches = displayText.toLowerCase().startsWith(filterValue.toLowerCase())
                    break
                  case 'endsWith':
                    matches = displayText.toLowerCase().endsWith(filterValue.toLowerCase())
                    break
                  default:
                    matches = displayText.toLowerCase().includes(filterValue.toLowerCase())
                }
                return matches
              })
            }
          }
        }
        
        const rootRows: ProcessedRowData[] = filteredData.map(item => ({
          ...item,
          group: true,
          [treepassKey]: [item.itemName || item.centerName || ''],
        }))
        
        setTimeout(() => {
          params.success({ rowData: rootRows })
        }, 100)
      } else {
        // Child level - call getChildRows function
        const parentKey = groupKeys[0]
        const parentData = processedList.find(item => 
          ((item as any).itemName || (item as any).centerName) === parentKey
        )
        
        if (parentData) {
          getChildRows(parentData as any, parentKey)
            .then((childRows) => {
              if (childRows && childRows.length > 0) {
                let filteredChildren = childRows
                
                // Apply filter to child rows if exists
                if (filterModel && filterModel['ag-Grid-AutoColumn']) {
                  const filter = filterModel['ag-Grid-AutoColumn']

                  // Handle multi filter (agMultiColumnFilter)
                  if (filter.filterType === 'multi' && filter.filterModels && filter.filterModels.length > 0) {

                    // Get the first filter model (set filter)
                    const setFilter = filter.filterModels[0]
                    if (setFilter.values !== undefined) {
                      if (setFilter.values.length > 0) {
                        // With child rows, we need to check if the parent is selected
                        const parentDisplayText = parentData.displayText || ''
                        const isParentSelected = setFilter.values.includes(parentDisplayText)
                        if (isParentSelected) {
                          // Parent is selected - show all child rows
                          filteredChildren = childRows
                        } else {
                          // Parent is not selected - hide all child rows
                          filteredChildren = []
                        }
                      } else {
                        // Empty array - no item selected, hide all
                        filteredChildren = []
                      }
                    }
                  }
                  // Handle single filter (legacy)
                  else if (filter.values && filter.values.length > 0) {
                    const parentDisplayText = parentData.displayText || ''
                    const isParentSelected = filter.values.includes(parentDisplayText)
                    
                    if (isParentSelected) {
                      filteredChildren = childRows
                    } else {
                      filteredChildren = []
                    }
                  }
                  // Handle text filter
                  else if (filter.filter) {
                    const filterValue = filter.filter
                    filteredChildren = childRows.filter(child => {
                      const childAny = child as any
                      let childDisplayText = ''
                      
                      if (childAny.itemName) {
                        childDisplayText = childAny.itemCode 
                          ? `${childAny.itemName} (ID：${childAny.itemCode})` 
                          : childAny.itemName
                      } else if (childAny.centerName) {
                        childDisplayText = childAny.centerId 
                          ? `${childAny.centerName} (ID：${childAny.centerId})` 
                          : childAny.centerName
                      } else if (childAny.customerCompanyName) {
                        childDisplayText = childAny.customerCompanyId 
                          ? `${childAny.customerCompanyName} (ID：${childAny.customerCompanyId})` 
                          : childAny.customerCompanyName
                      }
                      
                      let matches = false
                      switch (filter.type) {
                        case 'contains':
                          matches = childDisplayText.toLowerCase().includes(filterValue.toLowerCase())
                          break
                        case 'equals':
                          matches = childDisplayText === filterValue
                          break
                        case 'startsWith':
                          matches = childDisplayText.toLowerCase().startsWith(filterValue.toLowerCase())
                          break
                        case 'endsWith':
                          matches = childDisplayText.toLowerCase().endsWith(filterValue.toLowerCase())
                          break
                        default:
                          matches = childDisplayText.toLowerCase().includes(filterValue.toLowerCase())
                      }
                      return matches
                    })
                  }
                }
                
                const processedChildren: ProcessedRowData[] = filteredChildren.map(child => {
                  let childDisplayText = ''
                  const childAny = child as any
                  
                  if (childAny.itemName) {
                    childDisplayText = childAny.itemCode 
                      ? `${childAny.itemName} (ID：${childAny.itemCode})` 
                      : childAny.itemName
                  } else if (childAny.centerName) {
                    childDisplayText = childAny.centerId 
                      ? `${childAny.centerName} (ID：${childAny.centerId})` 
                      : childAny.centerName
                  } else if (childAny.customerCompanyName) {
                    childDisplayText = childAny.customerCompanyId 
                      ? `${childAny.customerCompanyName} (ID：${childAny.customerCompanyId})` 
                      : childAny.customerCompanyName
                  }
                  
                  return {
                    ...child,
                    displayText: childDisplayText,
                    group: false,
                    [treepassKey]: [...(parentData as any)[treepassKey], childAny.customerCompanyName || ''],
                  }
                })
                
                setTimeout(() => {
                  params.success({ rowData: processedChildren })
                }, 100)
              } else {
                setTimeout(() => {
                  params.success({ rowData: [] })
                }, 100)
              }
            })
            .catch((error) => {
              setTimeout(() => {
                params.fail()
              }, 100)
            })
        } else {
          setTimeout(() => {
            params.success({ rowData: [] })
          }, 100)
        }
      }
    },
  }), [processedList, getChildRows, treepassKey, hasValidData])

  // Empty datasource for no data case
  const createEmptyDatasource = useCallback(() => ({
    getRows: (params: GridParams) => {
      setTimeout(() => {
        params.success({ rowData: [] })
        // Manually trigger no data overlay after empty data is loaded
        setTimeout(() => {
          const api = gridRef.current?.api
          if (api && !api.isDestroyed()) {
            api.showNoRowsOverlay()
          }
        }, 100)
      }, 100)
    },
  }), [])

  // Get appropriate datasource based on data validity
  const getDatasource = useCallback(() => 
    hasValidData ? createServerSideDatasource() : createEmptyDatasource()
  , [hasValidData, createServerSideDatasource, createEmptyDatasource])

  // Setup grid with server-side datasource
  const onGridReady = useCallback((params: any) => {
    baseOnGridReady({
      ...params,
      api: {
        ...params.api,
        updateGridOptions: (options: ManagedGridOptions<any>) => {
          const { rowDragManaged, ...filteredOptions } = options || {}
          return params.api.updateGridOptions(filteredOptions)
        }
      }
    })
  }, [baseOnGridReady])
  
  // SSRM Tree Data callback functions
  const isServerSideGroup = useCallback((dataItem: any) => 
    dataItem.group === true
  , [])

  const getServerSideGroupKey = useCallback((dataItem: any) => 
    dataItem.itemName || dataItem.centerName || dataItem.customerCompanyName || ''
  , [])

  const isServerSideGroupOpenByDefault = useCallback(() => 
    false
  , [])

  // Auto group column definition - use field displayText instead of innerRenderer
  const autoGroupColumnDef = useMemo(() => ({
    headerName: 'グループ',
    field: 'displayText',
    width: 400, // Use width instead of minWidth to allow reset
    pinned: 'left' as const,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agSetColumnFilter',
          filterParams: {
            values: processedList.map(item => item.displayText),
            refreshValuesOnOpen: true,
            applyMiniFilterWhileTyping: true,
            excelMode: 'mac',
          },
        },
      ],
      buttons: ['reset'],
      closeOnApply: true,
      applyMiniFilterWhileTyping: true,
      excelMode: 'mac',
    },
    sortable: true,
  } as any), [processedList])

  // Generate columns with proper grouping
  const columnDefs = useMemo(() => {
    const groupByRectype = _.groupBy(sortedDayList, 'rectype')
    
    return [
      // Rating column group
      {
        headerName: '予測精度',
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        children: [
          {
            headerName: '',
            headerComponent: ExpectedRankTooltip,
            field: 'expectedRank',
            width: 90,
            cellRenderer: RatingRenderer,
            suppressHeaderMenuButton: true,
            sortable: false,
            filter: false,
            valueFormatter: (params: any) => params.value || '',
          } as any,
        ],
      } as any,
      // Dynamic date columns grouped by time period
      ...Object.entries(groupByRectype)
        .filter(([, dayListItems]) => dayListItems.length > 0)
        .map(([rectypeKey, dayListItems]) => {
          const recTypeName = dayListItems[0]?.rectypeName || rectypeKey
          return {
            headerName: recTypeName,
            headerClass: clsx(GROUP_HEADER_CLASS_MAP[rectypeKey]),
            suppressHeaderMenuButton: true,
            sortable: false,
            filter: false,
            children: dayListItems.map(dayItem => {
              const datevalue = new Date(dayItem.datevalue!)
              return {
                headerName: dateToStr24HPad0DayOfWeek(dayItem.datevalue!, 'MM/DD (WW)', false),
                field: dayItem.colname!,
                width: 95,
                type: 'rightAligned',
                headerClass: clsx(GROUP_HEADER_CLASS_MAP[rectypeKey], {
                  sunday: datevalue.getDay() === 0,
                }),
                cellClass: getCellClasses,
                valueFormatter: numberValueFormatter,
                suppressHeaderMenuButton: true,
                sortable: false,
                filter: false,
              } as any
            }),
          } as any
        }),
    ]
  }, [sortedDayList, getCellClasses, numberValueFormatter])

  // Update datasource when data changes
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    
    // manage loading by grid option (v34)
    api.setGridOption('loading', !!isLoading)
    if (isLoading) return

    // end loading: attach datasource then refresh after a tick to avoid race
    const datasource = getDatasource()
    api.setGridOption('serverSideDatasource', datasource)

    queueMicrotask(() => {
      if (api.isDestroyed()) return
      try {
        api.refreshServerSide({ purge: true })
      } catch (error) {
        api.setGridOption('serverSideDatasource', datasource)
      }
    })
  }, [processedList, sortedDayList, getDatasource, isLoading])

  // Auto-size columns when data is available and no saved state exists
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed() || isLoading || !hasValidData) return undefined
    
    // For server-side row model with tree data, we need to wait for the first data to be rendered
    // and then trigger auto-sizing if no saved column state exists
    if (autoSizeStrategy?.type === 'fitCellContents') {
      // Add delay to ensure server-side data is rendered
      const timer = setTimeout(() => {
        if (!api.isDestroyed()) {
          // Auto-size all columns including the auto-group column
          const allColumns = api.getColumns()
          if (allColumns && allColumns.length > 0) {
            const allColumnIds = allColumns.map(col => col.getColId())
            api.autoSizeColumns(allColumnIds)
            
            // Also specifically reset the auto-group column width to auto-fit
            const autoGroupColumn = allColumns.find(col => col.getColId() === 'ag-Grid-AutoColumn')
            if (autoGroupColumn) {
              api.autoSizeColumns(['ag-Grid-AutoColumn'])
            }
          }
        }
      }, 500) // Longer delay for server-side data
      
      return () => clearTimeout(timer)
    }
    
    return undefined
  }, [autoSizeStrategy, hasValidData, isLoading, processedList, sortedDayList])

  // Calculate dynamic height based on ItemDetail state and SearchBox visibility
  const containerHeight = useMemo(() => {
    if (treepassKey === 'treepassCenter' && !isItemDetailExpanded) {
      // For ShipExpectedCenter - use fixed height
      return GRID_CONFIG.CONTAINER_HEIGHT.treepassCenter
    }
    
    // For ShipExpectedItem - adjust height based on ItemDetail state
    const baseHeight = 200 // Base height when ItemDetail is collapsed
    const itemDetailHeight = isItemDetailExpanded 
      ? GRID_CONFIG.ITEM_DETAIL_HEIGHT.expanded 
      : GRID_CONFIG.ITEM_DETAIL_HEIGHT.collapsed
    const heightAdjustment = itemDetailHeight - GRID_CONFIG.ITEM_DETAIL_HEIGHT.collapsed
    return `calc(100vh - ${baseHeight + heightAdjustment}px)`
  }, [treepassKey, isItemDetailExpanded])

  // Memoized styles
  const containerStyle = useMemo(() => ({ 
    width: '100%', 
    height: containerHeight
  }), [containerHeight])
  
  const gridStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])

  const defaultColDef = useMemo(() => ({
    resizable: true,
    suppressHeaderMenuButton: true,
    sortable: false,
    filter: false,
  }), [])

  return (
    <>
      <ShowLastUpdateDatetime<ShipExpected> list={list.list} lastUpdateDatetime={list.lastUpdateDatetime} />
      <div style={containerStyle}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            onGridReady={onGridReady}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            rowModelType="serverSide"
            treeData
            isServerSideGroup={isServerSideGroup}
            getServerSideGroupKey={getServerSideGroupKey}
            isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
            suppressAggFuncInHeader
            suppressCellFocus
            suppressMenuHide
            suppressContextMenu
            rowHeight={GRID_CONFIG.ROW_HEIGHT}
            suppressHorizontalScroll={false}
            suppressColumnVirtualisation={false}
            suppressRowVirtualisation={false}
            loading={isLoading}
            autoSizeStrategy={autoSizeStrategy}
          />
        </div>
      </div>
    </>
  )
}

export default ShipExpectedDataList
